import React, { ChangeEvent, useRef, useState } from 'react'
import { Box, ListItemIcon, ListItemText, Menu, MenuItem, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { IconCamera, IconPhoto } from '@tabler/icons-react'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import { errorLog } from '../utils/log-helper'
import { logEvent } from '../analytics/analytics'
import { SANS_FONT, SERIF_FONT } from '../theme'
import {
  emitCameraPromptIfNeeded,
  emitCameraDeniedIfDenied,
} from '../helpers/camera-analytics'
import MenuScanCoachingSheet, {
  useMenuScanCoaching,
} from './MenuScanCoachingSheet'

/** How the menu image was captured, so the scan flow can attribute the source instead of 'unknown'. */
export type MenuScanUploadMethod = 'camera' | 'file_picker'

export interface MenuScanBannerProps {
  onFileSelected?: (file: File, uploadMethod?: MenuScanUploadMethod) => void
  /** Optional restaurant alias to pre-associate the scan (e.g. when shown on restaurant page). */
  restaurantAlias?: string
  /** Where this banner lives — standardizes the menu_scan_opened entry_point (see events.ts). */
  entryPoint?: 'home_banner' | 'restaurant_detail'
  /** When true, prevents opening file picker (e.g. while upload in progress). */
  disabled?: boolean
  /**
   * When false, the restaurant has no menu yet — banner becomes a high-urgency primary CTA
   * explaining that scanning is required to unlock recommendations. Defaults to true (has menu).
   */
  hasMenu?: boolean
  /** Override the headline text (replaces default copy for the active hasMenu state). */
  headline?: string
  /** Override the subtext shown on all screen sizes. */
  subtext?: string
  /** On mobile (xs), show this text instead of subtext — use when desktop/mobile context differs. */
  mobileSubtext?: string
  /** Override the CTA button label. */
  buttonText?: string
  /** Hide the CTA button on mobile (xs) screens — use when a persistent mobile action already exists. */
  hideButtonOnMobile?: boolean
}

const MenuScanBanner: React.FC<MenuScanBannerProps> = ({
  onFileSelected,
  restaurantAlias,
  entryPoint,
  disabled = false,
  hasMenu = true,
  headline,
  subtext,
  mobileSubtext,
  buttonText,
  hideButtonOnMobile = false,
}) => {
  const theme = useTheme()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const { requestCapture, coachingProps } = useMenuScanCoaching(
    'menu_scan_banner',
    entryPoint === 'restaurant_detail' ? 'restaurant_detail' : 'home_banner',
  )

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (event?.target?.files && event.target.files[0]) {
      const file = event.target.files[0]

      // Web file input is always the library/file picker.
      if (onFileSelected) {
        onFileSelected(file, 'file_picker')
      }

      // Reset the input so the same file can be selected again
      event.target.value = ''
    }
  }

  const handleNativePhoto = async (source: CameraSource): Promise<void> => {
    setMenuAnchor(null)
    const isCamera = source === CameraSource.Camera
    try {
      if (isCamera) {
        await emitCameraPromptIfNeeded('menu_scan_banner')
      }
      logEvent('menu_scan_capture_shown', { screen_name: 'menu_scan_banner' })
      const photo = await Camera.getPhoto({
        quality: 90,
        resultType: CameraResultType.DataUrl,
        source,
        allowEditing: false,
        promptLabelHeader: 'Scan a menu',
        promptLabelPhoto: 'Choose menu photo',
        promptLabelPicture: 'Take menu photo',
      })
      if (isCamera) {
        logEvent('camera_permission', { status: 'granted', screen_name: 'menu_scan_banner' })
      }
      if (!photo.dataUrl) return
      const res = await fetch(photo.dataUrl)
      const blob = await res.blob()
      const file = new File(
        [blob],
        `menu-${Date.now()}.jpg`,
        { type: blob.type || 'image/jpeg' },
      )
      onFileSelected?.(file, isCamera ? 'camera' : 'file_picker')
    } catch (e) {
      if (isCamera) {
        void emitCameraDeniedIfDenied('menu_scan_banner')
      }
      errorLog('[MenuScanBanner] Camera error', e)
    }
  }

  const handleMenuScanClick = (e: React.MouseEvent<HTMLElement>): void => {
    if (disabled) return
    logEvent('menu_scan_opened', { screen_name: 'menu_scan_banner', entry_point: entryPoint })
    // Retain the anchor so the native source picker can open from the banner after coaching.
    const anchor = e.currentTarget
    // Show the pre-capture coaching cue first; the existing capture path runs on confirm.
    requestCapture(() => {
      if (Capacitor.isNativePlatform()) {
        // Native shows the source picker (camera vs library); capture UI is emitted in
        // handleNativePhoto when getPhoto actually opens.
        setMenuAnchor(anchor)
      } else {
        logEvent('menu_scan_capture_shown', { screen_name: 'menu_scan_banner' })
        fileInputRef.current?.click()
      }
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleMenuScanClick(e as unknown as React.MouseEvent<HTMLElement>)
    }
  }

  const ariaLabel = hasMenu
    ? 'Help complete this menu — add missing dishes and earn points'
    : 'No menu available — see what to order and earn points'

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-disabled={disabled}
        onClick={handleMenuScanClick}
        onKeyDown={handleKeyDown}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: '12px', sm: '16px' },
          padding: { xs: '16px', sm: '20px' },
          marginTop: { xs: '12px', sm: '16px' },
          marginBottom: { xs: '12px', sm: '16px' },
          marginX: 'auto',
          maxWidth: '920px',
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          borderRadius: '20px',
          background: hasMenu
            ? 'linear-gradient(135deg, rgba(255, 245, 238, 0.5) 0%, rgba(252, 240, 230, 0.3) 100%)'
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
          border: hasMenu
            ? '1px solid rgba(210, 180, 160, 0.35)'
            : `1.5px solid ${alpha(theme.palette.primary.main, 0.4)}`,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: hasMenu
            ? '0 2px 8px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.05)'
            : `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}, 0 2px 8px rgba(0, 0, 0, 0.06)`,
          '&:hover': {
            boxShadow: hasMenu
              ? '0 4px 12px rgba(0, 0, 0, 0.08), 0 8px 16px rgba(0, 0, 0, 0.08)'
              : `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}, 0 4px 12px rgba(0, 0, 0, 0.08)`,
            background: hasMenu
              ? 'linear-gradient(135deg, rgba(255, 245, 238, 0.6) 0%, rgba(252, 240, 230, 0.4) 100%)'
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.16)} 100%)`,
          },
          '&:active': {
            transform: 'scale(0.98)',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: theme.palette.primary.main,
            outlineOffset: '2px',
          },
        }}
      >
        {/* Icon Block */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: '56px', sm: '56px' },
            height: { xs: '56px', sm: '56px' },
            minWidth: { xs: '56px', sm: '56px' },
            borderRadius: '14px',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            position: 'relative',
            boxShadow: hasMenu
              ? `0 2px 6px ${alpha(theme.palette.primary.main, 0.15)}`
              : `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '10px',
              height: '10px',
              background: 'white',
              borderRadius: '50%',
              opacity: 0.35,
              boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.2)',
            },
          }}
        >
          <IconCamera size={28} color="white" strokeWidth={1.8} />
        </Box>

        {/* Text Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{
              fontSize: { xs: '17px', sm: '18px' },
              fontWeight: 700,
              lineHeight: 1.3,
              color: '#1F2937',
              marginBottom: '4px',
              fontFamily: SERIF_FONT,
            }}
          >
            {headline ?? (hasMenu ? (
              'Help complete this menu'
            ) : (
              <>
                No menu yet —{' '}
                <span style={{ color: theme.palette.primary.main, fontWeight: 700 }}>be the first to unlock it</span>
              </>
            ))}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '14px', sm: '14px' },
              fontWeight: 500,
              lineHeight: 1.4,
              color: hasMenu ? '#6B7580' : '#4B5563',
              fontFamily: SANS_FONT,
            }}
          >
            {mobileSubtext ? (
              <>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {subtext ?? (hasMenu
                    ? 'Add missing dishes and earn points.'
                    : 'See what to order and earn points.')}
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  {mobileSubtext}
                </Box>
              </>
            ) : (
              subtext ?? (hasMenu
                ? 'Add missing dishes and earn points.'
                : 'See what to order and earn points.')
            )}
          </Typography>
        </Box>

        {/* CTA Button */}
        <Box
          sx={{
            display: hideButtonOnMobile ? { xs: 'none', sm: 'flex' } : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            minWidth: { xs: '100%', sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            minHeight: '48px',
            padding: { xs: '0 16px', sm: '0 20px' },
            borderRadius: '10px',
            backgroundColor: hasMenu ? 'white' : theme.palette.primary.main,
            color: hasMenu ? '#4B5563' : 'white',
            border: hasMenu ? '1.5px solid #D1D5DB' : 'none',
            fontWeight: 700,
            fontSize: { xs: '15px', sm: '15px' },
            fontFamily: SANS_FONT,
            whiteSpace: 'normal',
            transition: 'all 180ms cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: hasMenu
              ? 'none'
              : `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
            '&:hover': {
              backgroundColor: hasMenu ? '#F3F4F6' : theme.palette.primary.dark,
              transform: 'translateY(-1px)',
              boxShadow: hasMenu
                ? 'none'
                : `0 6px 16px ${alpha(theme.palette.primary.main, 0.5)}`,
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: hasMenu
                ? 'none'
                : `0 2px 6px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: theme.palette.primary.main,
              outlineOffset: '2px',
            },
          }}
        >
          {/* Camera Icon for Button */}
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle
              cx="12"
              cy="13"
              r="4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {buttonText ? (
            <Box component="span">{buttonText}</Box>
          ) : hasMenu ? (
            <Box component="span">Add dishes</Box>
          ) : (
            <Box component="span">See what to order</Box>
          )}
        </Box>
      </Box>

      {/* Hidden file input for web */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(event) => {
          void handleFileChange(event)
        }}
      />

      {/* Native source picker (camera vs library) */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={() => { handleNativePhoto(CameraSource.Camera).catch(() => {}) }}>
          <ListItemIcon><IconCamera size={20} /></ListItemIcon>
          <ListItemText>Take Photo</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleNativePhoto(CameraSource.Photos).catch(() => {}) }}>
          <ListItemIcon><IconPhoto size={20} /></ListItemIcon>
          <ListItemText>Choose from Library</ListItemText>
        </MenuItem>
      </Menu>

      {/* Pre-capture coaching cue (shared across all scan launch sites) */}
      <MenuScanCoachingSheet {...coachingProps} />
    </>
  )
}

export default MenuScanBanner
